import type { KeyboardEvent, MouseEvent } from "react";
import { Badge, BadgeSize, BadgeVariant } from "shared/components";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { cx } from "shared/utils";
import type { ListingHealthLabel } from "types/landlord/address";
import { LISTING_HEALTH_LABEL_TEXT, LISTING_HEALTH_TIP } from "./constants";

const VARIANT: Record<ListingHealthLabel, BadgeVariant> = {
  weak: BadgeVariant.ERROR,
  fair: BadgeVariant.WARNING,
  strong: BadgeVariant.SUCCESS
};

type BadgeActivateEvent =
  | MouseEvent<HTMLSpanElement>
  | KeyboardEvent<HTMLSpanElement>;

const buttonProps = (onActivate: (event: BadgeActivateEvent) => void) => ({
  role: "button",
  tabIndex: 0,
  onClick: onActivate,
  onKeyDown: (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onActivate(event);
  }
});

export const ListingHealthBadge = ({
  label,
  size = BadgeSize.SM,
  className,
  clickable = true,
  onClick
}: {
  label: ListingHealthLabel;
  size?: BadgeSize;
  className?: string;
  /** Off where the badge is only a label; table cells keep it on and let the grid handle the click. */
  clickable?: boolean;
  onClick?: (event: BadgeActivateEvent) => void;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span
        className={cx(
          "inline-flex w-fit",
          clickable && "cursor-pointer",
          className
        )}
        {...(clickable && onClick ? buttonProps(onClick) : {})}
      >
        <Badge variant={VARIANT[label]} size={size}>
          {LISTING_HEALTH_LABEL_TEXT[label]}
        </Badge>
      </span>
    </TooltipTrigger>
    <TooltipContent className="max-w-[320px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-content-1000-inverted">
      <p>{LISTING_HEALTH_TIP}</p>
    </TooltipContent>
  </Tooltip>
);
